import type { Type } from '@mend/types/appointment';
import type {
  GetActiveProviderStatsParams,
  GetAggregatedAppointmentDataParams,
  GetAggregatedFormDataParams,
  GetAppointmentSnapshotDataParams,
  GetFormCompletionTimeParams,
  GetFormsAssignedParams,
  GetFormsCompletedParams,
  GetFormsCompletionRateParams,
  GetFormSnapshotDataParams,
  GetNoShowRateParams,
  GetTotalAppointmentsParams,
  GetTotalPatientsSeenParams,
} from '#services/api/insights.ts';

import { queryOptions } from '@tanstack/react-query';

import { getAppointmentStatuses } from '#services/api/appointment-status.ts';
import { getAppointmentTypes } from '#services/api/appointment-type.ts';
import { getAssessments } from '#services/api/assessment.ts';
import {
  getActiveProviders,
  getActiveProviderStats,
  getAppointmentRates,
  getAppointmentsByType,
  getAppointmentSnapshotData,
  getFormCompletionByType,
  getFormCompletionTime,
  getFormCompletionTimeTrends,
  getFormsAssigned,
  getFormsCompleted,
  getFormsCompletionRate,
  getFormSnapshotData,
  getFormStatusCounts,
  getFormTrends,
  getFormTypeBreakdown,
  getNoShowRate,
  getTotalAppointments,
  getTotalPatientsSeen,
} from '#services/api/insights.ts';
import {
  getAutomaticOutreachConfig,
  type GetAutomaticOutreachConfigParams,
  getOutreachRun,
  getReadinessConfig,
  getRiskModelExplainer,
  isTerminalRunStatus,
  listWaitlist,
  listOfferableSlots,
  type ListOfferableSlotsParams,
  previewMessageContent,
  type PreviewMessageContentRequest,
} from '#services/api/mia.ts';
import {
  getPatientReadinessDetails,
  type GetPatientReadinessDetailsParams,
} from '#services/api/patient-readiness.ts';
import { getProperties } from '#services/api/properties.ts';
import { getProviderDirectory } from '#services/api/provider.ts';
import { getSavedViews } from '#services/api/saved-views.ts';
import {
  getTransferQueueAppointments,
  type GetTransferQueueAppointmentsParams,
  getTransferQueueStats,
  type GetTransferQueueStatsParams,
} from '#services/api/transfer-queue.ts';
import { getMe } from '#services/api/user.ts';

export const me = queryOptions({
  queryKey: ['users', 'me'],
  queryFn: getMe,
  staleTime: Infinity,
});

export const properties = queryOptions({
  queryKey: ['properties'],
  queryFn: getProperties,
  staleTime: Infinity,
});

export const savedViews = queryOptions({
  queryKey: ['savedViews'],
  queryFn: getSavedViews,
  staleTime: Infinity,
});

export const appointmentStatuses = queryOptions({
  queryKey: ['appointment-statuses'],
  queryFn: getAppointmentStatuses,
  staleTime: Infinity,
});

export function transferQueueStats(params: GetTransferQueueStatsParams) {
  return queryOptions({
    queryKey: ['transfer-queue-stats', params],
    queryFn: () => getTransferQueueStats(params),
  });
}

export function transferQueueAppointments(params: GetTransferQueueAppointmentsParams) {
  return queryOptions({
    queryKey: ['transfer-queue-appointments', params],
    queryFn: () => getTransferQueueAppointments(params),
  });
}

/**
 * Every provider the Provider Filter can name, for the picker's options and its chips alike.
 *
 * Held for the session and filtered client-side, so the picker lists the organization on open
 * instead of searching the API per keystroke.
 *
 * Like every other cache here it is not scoped to an organization, so switching organization in the
 * portal — the session cookie is shared by every Mend web app on the parent domain — leaves the
 * previous organization's providers in an already-open tab. Accepted knowingly, and app-wide rather
 * than particular to this query; worth fixing for all of them at once, not here.
 */
export const providerDirectory = queryOptions({
  queryKey: ['providers', 'directory'],
  queryFn: getProviderDirectory,
  staleTime: Infinity,
});

const patientReadinessPollingIntervalMs = 5 * 60 * 1_000; // 5 minutes

/**
 * Key prefixes for the reads a debug seed run makes stale, so a caller can invalidate every
 * parameterised variant at once without restating the filters each was cached under. Defined here
 * beside the factories that use them: a renamed key that left a caller invalidating a prefix
 * nothing is cached under would fail silently, as a feed that simply never refreshed.
 */
export const seededReadQueryKeys = {
  patientReadinessDetails: ['patient-readiness-details'],
  offerableSlots: ['offerable-slots'],
  debugSeederPreflight: ['debug-seeder-preflight'],
} as const;

export function patientReadinessDetails(params: GetPatientReadinessDetailsParams) {
  return queryOptions({
    queryKey: [...seededReadQueryKeys.patientReadinessDetails, params],
    queryFn: () => getPatientReadinessDetails(params),
    staleTime: 0,
    refetchOnMount: true,
    // The worklist reflects live confirmation state (patients confirm out-of-band), so keep
    // polling — there is no terminal "snapshot" state that would make refetching pointless.
    refetchInterval: patientReadinessPollingIntervalMs,
  });
}

export function miaMessagePreview(request: PreviewMessageContentRequest) {
  return queryOptions({
    queryKey: ['mia-message-preview', request],
    queryFn: () => previewMessageContent(request),
    // The generated copy is stable for a given appointment/channel — don't re-generate (and
    // re-bill the LLM) every time the preview is shown again in the same session.
    staleTime: Infinity,
  });
}

export function riskModelExplainer(params: { orgId: number }) {
  return queryOptions({
    queryKey: ['risk-model-explainer', params],
    queryFn: () => getRiskModelExplainer(params),
    // The deployed model's feature importance is refreshed by the analytics layer on its own
    // (daily) cadence — nothing a session can do changes it.
    staleTime: Infinity,
  });
}

/** How often an in-flight Outreach Run is re-read. Stops entirely once the run is terminal. */
const outreachRunPollingIntervalMs = 5_000;

/**
 * One Outreach Run's current state, polled while it is in flight.
 *
 * The workflow stream carries per-target patches and no run-level completion event, so "is this run
 * finished" is a question only the run resource answers. Deriving it client-side by counting
 * settled targets would strand any caller that missed a patch across a stream reconnect — the run
 * would never appear to finish. Polling stops the moment the status is terminal, so an idle run
 * costs nothing.
 */
export function outreachRun(params: { orgId: number; runId: number }) {
  return queryOptions({
    queryKey: ['outreach-run', params],
    queryFn: () => getOutreachRun(params),
    refetchInterval: (query) =>
      query.state.data && isTerminalRunStatus(query.state.data.status)
        ? false
        : outreachRunPollingIntervalMs,
  });
}

export function readinessConfig(params: { orgId: number }) {
  return queryOptions({
    queryKey: ['readiness-config', params],
    queryFn: () => getReadinessConfig(params),
    // Any of the org's admins can change this, from any session — so a saved threshold arrives
    // out-of-band exactly like a patient's confirmation does. Polled on the worklist's interval
    // rather than a longer one because the two are applied to each other: the briefing flags rows
    // at or above this threshold, and refreshing the rows while holding an hour-old floor would
    // judge new appointments by a threshold nobody is using any more.
    staleTime: 0,
    refetchOnMount: true,
    refetchInterval: patientReadinessPollingIntervalMs,
  });
}

export function automaticOutreachConfig(params: GetAutomaticOutreachConfigParams) {
  return queryOptions({
    queryKey: ['automatic-outreach-config', params],
    queryFn: () => getAutomaticOutreachConfig(params),
    staleTime: 0,
    refetchOnMount: true,
    refetchInterval: patientReadinessPollingIntervalMs,
  });
}

/** Every active enrollment in the authorized organization, in server waiting order. */
export function activeWaitlist(params: { orgId: number }) {
  // Ignore dashboard filters and Displayed Providers so new out-of-filter enrollments stay visible.
  // Product may revisit this organization-wide scope later.
  return queryOptions({
    queryKey: ['active-waitlist', params],
    queryFn: () =>
      listWaitlist({
        orgId: params.orgId,
        status: 'ACTIVE',
        sortBy: 'createdAt',
        sortDirection: 'asc',
      }),
    staleTime: 0,
    refetchOnMount: true,
  });
}

export function offerableSlots(params: ListOfferableSlotsParams) {
  return queryOptions({
    queryKey: [...seededReadQueryKeys.offerableSlots, params],
    queryFn: () => listOfferableSlots(params),
    staleTime: 0,
    refetchOnMount: true,
    // A slot's offer state can change from a patient's reply, not just a staff click — keep
    // polling like patientReadinessDetails rather than relying solely on post-mutation refetches.
    refetchInterval: patientReadinessPollingIntervalMs,
  });
}

export function formTrends(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formTrends', params],
    queryFn: () => getFormTrends(params),
    staleTime: Infinity,
  });
}

export function formCompletionByType(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formCompletionByType', params],
    queryFn: () => getFormCompletionByType(params),
    staleTime: Infinity,
  });
}

export function appointmentRates(params: GetAggregatedAppointmentDataParams) {
  return queryOptions({
    queryKey: ['appointmentRates', params],
    queryFn: () => getAppointmentRates(params),
    staleTime: Infinity,
  });
}

export function appointmentsByType(params: GetAggregatedAppointmentDataParams) {
  return queryOptions({
    queryKey: ['appointmentsByType', params],
    queryFn: () => getAppointmentsByType(params),
    staleTime: Infinity,
  });
}

export function noShowRate(params: GetNoShowRateParams) {
  return queryOptions({
    queryKey: ['noShowRate', params],
    queryFn: () => getNoShowRate(params),
    staleTime: Infinity,
  });
}

export function appointmentSnapshot(params: GetAppointmentSnapshotDataParams) {
  return queryOptions({
    queryKey: ['appointmentSnapshot', params],
    queryFn: () => getAppointmentSnapshotData(params),
    staleTime: Infinity,
  });
}

export function totalPatientsSeen(params: GetTotalPatientsSeenParams) {
  return queryOptions({
    queryKey: ['totalPatientsSeen', params],
    queryFn: () => getTotalPatientsSeen(params),
    staleTime: Infinity,
  });
}

export function formCompletionTime(params: GetFormCompletionTimeParams) {
  return queryOptions({
    queryKey: ['formCompletionTime', params],
    queryFn: () => getFormCompletionTime(params),
    staleTime: Infinity,
  });
}

export function activeProviders(params: GetAggregatedAppointmentDataParams) {
  return queryOptions({
    queryKey: ['activeProviders', params],
    queryFn: () => getActiveProviders(params),
    staleTime: Infinity,
  });
}

export function activeProviderStats(params: GetActiveProviderStatsParams) {
  return queryOptions({
    queryKey: ['activeProviderStats', params],
    queryFn: () => getActiveProviderStats(params),
    staleTime: Infinity,
  });
}

export function formCompletionTimeTrends(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formCompletionTimeTrends', params],
    queryFn: () => getFormCompletionTimeTrends(params),
    staleTime: Infinity,
  });
}

export function formTypeBreakdown(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formTypeBreakdown', params],
    queryFn: () => getFormTypeBreakdown(params),
    staleTime: Infinity,
  });
}

export function formStatusCounts(params: GetAggregatedFormDataParams) {
  return queryOptions({
    queryKey: ['formStatusCounts', params],
    queryFn: () => getFormStatusCounts(params),
    staleTime: Infinity,
  });
}

export function formSnapshot(params: GetFormSnapshotDataParams) {
  return queryOptions({
    queryKey: ['formSnapshot', params],
    queryFn: () => getFormSnapshotData(params),
    staleTime: Infinity,
  });
}

export function appointmentTypes(type?: Type) {
  return queryOptions({
    queryKey: ['appointment-types', type],
    queryFn: () => getAppointmentTypes(type),
    staleTime: Infinity,
  });
}

export const assessments = queryOptions({
  queryKey: ['assessments'],
  queryFn: getAssessments,
  staleTime: Infinity,
});

export function formsAssigned(params: GetFormsAssignedParams) {
  return queryOptions({
    queryKey: ['formsAssigned', params],
    queryFn: () => getFormsAssigned(params),
    staleTime: Infinity,
  });
}

export function formsCompleted(params: GetFormsCompletedParams) {
  return queryOptions({
    queryKey: ['formsCompleted', params],
    queryFn: () => getFormsCompleted(params),
    staleTime: Infinity,
  });
}

export function formsCompletionRate(params: GetFormsCompletionRateParams) {
  return queryOptions({
    queryKey: ['formsCompletionRate', params],
    queryFn: () => getFormsCompletionRate(params),
    staleTime: Infinity,
  });
}

export function totalAppointments(params: GetTotalAppointmentsParams) {
  return queryOptions({
    queryKey: ['totalAppointments', params],
    queryFn: () => getTotalAppointments(params),
    staleTime: Infinity,
  });
}
